import api from '../../../services/apiConfig';

// --- Interfaces de Pedidos ---

export interface Order {
    ID: number;
    COD_TEOREMA: number;
    cliente: string;
    TIPO_PEDIDO: string;
    EMISSAO: string;
    PREVISAO: string;
    SAIDA: string | null;
    NOTA_FISCAL: string | null;
    SITUACAO: string;
    OPERACAO_FISCAL?: string;
    VALOR_TOTAL?: number;
    CARTEIRA?: string;
}
export interface OrderKpis {
    totalPedidos: number;
    faturamentoTotal: number;
    totalPares: number;
}
export interface PaginationInfo {
    currentPage: number;
    totalPages: number;
    totalItems: number;
}
export interface OrdersApiResponse {
    orders: Order[];
    kpis: OrderKpis;
    pagination: PaginationInfo;
}

export interface Situation {
    ID: number;
    DESCRIÇÃO: string;
    total: number;
}

export interface Segmento {
    ID: number;
    DESCRIÇÃO: string;
}

export interface Linha {
    DESCRIÇÃO: string;
}

export interface OrderItem {
    Linha: string;
    Item: string;
    Quantidade: number;
    PrecoUnitario: number;
    ValorTotal: number;
}

// =================================================================
// MODIFICADO: FullOrderDetails
// A API envia o objeto 'pedido' flat (plano)
// O objeto 'cliente' foi removido da tipagem, 
// pois seus campos estão dentro de 'pedido'.
// =================================================================
export interface LookupItem {
    ID: number;
    DESCRIÇÃO: string;
}

export interface FullOrderDetails {
    pedido: {
        ID: number;
        OPERACAO: number;
        OPERACAO_FISCAL: number;
        COD_TEOREMA: string; // O campo PEDIDO na imagem
        DOCUMENTO: string; // NOTA FISCAL
        EMISSAO: string;
        PREVISAO: string;
        DATA_NOTA: string;
        SITUAÇÃO: number;
        CLIENTE: number; // ID
        VENDEDOR: number;
        COND_PAGAMENTO: number;
        PRAZO_MEDIO: number;
        OC: string;
        FRETE_TIPO: number;
        TRANSPORTADORA: string;
        REDESPACHO: number;
        COD_RASTREIO: string;
        MC_TOTAL: number;

        // Campos de Entrega
        ENTREGA_NOME: string;
        ENTREGA_CNPJ: string;
        ENTREGA_INSCR: string;
        ENTREGA_ENDERECO: string;
        ENTREGA_NUMERO: string;
        ENTREGA_COMPLEMENTO: string;
        ENTREGA_BAIRRO: string;
        ENTREGA_CEP: string;
        ENTREGA_CIDADE: string;
        ENTREGA_UF: string;

        // Campos do Cliente (vindos do JOIN)
        CLIENTE_NOME: string;
        CLIENTE_CNPJ: string;
        CLIENTE_IE: string;
        CLIENTE_ENDERECO: string;
        CLIENTE_NUMERO: string;
        CLIENTE_COMPLEMENTO: string;
        CLIENTE_BAIRRO: string;
        CLIENTE_CEP: string;
        CLIENTE_CIDADE: string;
        CLIENTE_UF: string;
        CLIENTE_TELEFONE: string;
        CLIENTE_EMAIL: string;
        CLIENTE_SEGMENTO_ID: number;
    };
    resumo: {
        TotalQuant: number;
        TotalValorProdutos: number;
        MC_TOTAL?: number; // Adicionar este campo
    };
    items: OrderItem[];
    lookups: {
        operacoes: LookupItem[];
        situacoes: LookupItem[];
    };
    cotacoes_fretes?: any[];
    relacionamento?: any[];
}


// --- Funções de Fetch (Rotas Corrigidas) ---

// Corresponde a: GET /api/orders
export const fetchOrders = async (
    initialDate: string,
    finalDate: string,
    page: number,
    limit: number,
    search?: string,
    statusId?: string,
    carteiraId?: string,
    tipoPedido?: string,
    preset?: string,
    segmentoId?: string,
    linha?: string
) => {
    // Rota corrigida: de '/sales/orders' para '/orders'
    return api.get('/orders', {
        params: { dataI: initialDate, dataF: finalDate, page, limit, search, statusId, carteiraId, tipoPedido, preset, segmentoId, linha } // <-- MODIFICAR
    });
};


// Corresponde a: GET /api/orders/situations
export const fetchSituations = async (dataI: string, dataF: string, carteiraId?: string, tipoPedido?: string, preset?: string, segmentoId?: string, linha?: string) => { // <-- MODIFICAR
    // Rota corrigida: de '/sales/orders/situations' para '/orders/situations'
    return api.get<Situation[]>('/orders/situations', {
        params: { dataI, dataF, carteiraId, tipoPedido, preset, segmentoId, linha } // <-- MODIFICAR
    });
};

// Corresponde a: GET /api/orders/segments
export const fetchSegments = async () => {
    return api.get<Segmento[]>('/orders/segments');
};

export const fetchLinhas = async () => {
    return api.get<Linha[]>('/orders/linhas');
};

// =================================================================
// MODIFICADO: fetchOrderDetails
// =================================================================
// Corresponde a: GET /api/orders/:orderId
export const fetchOrderDetails = async (orderId: number) => {
    // Retorna a nova estrutura de dados completa
    return api.get<FullOrderDetails>(`/orders/${orderId}`);
};

export const closeOrder = async (orderId: number) => {
    return api.put(`/orders/${orderId}/close`);
};

export const updateOrderOperacao = async (orderId: number, operacao: number) => {
    return api.patch(`/orders/${orderId}/operacao`, { operacao });
};

export interface GradeItemPayload {
    itemId: number;
    grade: { [tamanho: number]: number };
}

export const updateOrderGrade = async (orderId: number, items: GradeItemPayload[]) => {
    return api.patch(`/orders/${orderId}/grade`, { items });
};

export interface NewOrderItemPayload {
    produto_id: number;
    referencia: string;
    descricao: string;
    tabela: string;
    comissao: number;
    com_imb: number;
    preco: number;
    base_cost: number;
    opcionais: string;
    cod_pcp: string;
    unidade: string;
    sizes: { [key: number]: number };
}

export const addItemsToOrder = async (orderId: number, items: NewOrderItemPayload[]) => {
    return api.post(`/orders/${orderId}/items`, { items });
};

// --- Evolução de Pedidos ---
export interface EvolutionData {
    mes: string;
    totalPedidos: number;
    faturamento: number;
    pares: number;
}

export const fetchOrderEvolution = async (
    dataI: string,
    dataF: string,
    statusId?: string,
    carteiraId?: string,
    tipoPedido?: string,
    preset?: string,
    segmentoId?: string,
    linha?: string
) => {
    return api.get<EvolutionData[]>('/orders/evolution', {
        params: { dataI, dataF, statusId, carteiraId, tipoPedido, preset, segmentoId, linha }
    });
};